import { Injectable, EventEmitter } from '@angular/core';
import { ToastrService, IndividualConfig } from 'ngx-toastr';
import * as CryptoJS from 'crypto-js';
import { LocationStrategy } from '@angular/common';
import { UserRoles } from '../pages/common-models/common.model';
import { ClientMenuList, ClientServicesBillingQ, ClientServicesOpenCases, MenuList, PatientMenuList } from '../pages/layout/layout-json';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { clients, patientsIdTypes, workBenchClinicalQ, workBenchInventories } from '../pages/layout/layout-json';
import { DomSanitizer } from '@angular/platform-browser';
import { CaseInfoPopUpComponent } from '../pages/common-pop-up/case-info-pop-up/case-info-pop-up.component';
import { ClientDetailsComponent } from '../pages/common-pop-up/client-details/client-details.component';
import { PatientDetailsComponent } from '../pages/common-pop-up/patient-details/patient-details.component';
import { PatientLogDialogComponent } from '../pages/common-pop-up/patient-log/patient-log.component';
import { PhysicianDetailsComponent } from '../pages/common-pop-up/physician-details/physician-details.component';
import { ViewLogsComponent } from '../pages/common-pop-up/view-logs/view-logs.component';
import { ViewPatientHistoryComponent } from '../pages/common-pop-up/view-patient-history/view-patient-history.component';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  siteName = 'Omnia LIMS';
  mainMenu = [];
  currentUserRole = new UserRoles();
  clientMenu = [];
  currentUser: any = {};
  userPermissions = [];
  pathName = '';

  isPopupOpened = new EventEmitter<{ isPopupOpened: boolean }>();

  showErrorMessage = new EventEmitter<{ error: string, displayError: boolean }>();
  showSuccessMessage = new EventEmitter<{ success: string, displaySuccess: boolean }>();
  showInfoMessage = new EventEmitter<{ info: string, displayInfo: boolean }>();

  showPopupErrorMessage = new EventEmitter<{ error: string, displayError: boolean }>();
  showPopupSuccessMessage = new EventEmitter<{ success: string, displaySuccess: boolean }>();
  showPopupInfoMessage = new EventEmitter<{ info: string, displayInfo: boolean }>();

  caseTypesList = [
    { id: 0, name: 'Tox' },
    { id: 1, name: 'Clinical' },
    { id: 3, name: 'PCR' },
    { id: 4, name: 'PGX' }
  ];

  ruleTypesList = [
    { id: 1, name: 'Amplification' },
    { id: 2, name: 'Range' },
    { id: 3, name: 'Data Point' }
  ];

  amplificationTypesList = [
    { id: 1, name: 'Amplified' },
    { id: 2, name: 'Not Amplified' }
  ];

  labTypesList = [
    { id: 1, name: 'Tox' },
    { id: 2, name: 'Chemistry' },
    { id: 3, name: 'Molecular' }
  ];

  priorityList = [
    { id: 1, name: 'Low' },
    { id: 2, name: 'High' }
  ];

  frequencyList = [
    { id: 1, name: 'Weekly' },
    { id: 2, name: 'Monthly' },
    { id: 3, name: 'Annual' },
    { id: 4, name: 'Days' },
  ];

  insuranceTypesList = ['Primary', 'Secondary', 'Tertiary'];
  genderList = ['Male', 'Female', 'Unspecified'];
  maritalStatus = ['Single', 'Married'];
  insurancePlanTypesList = ['HMO', 'PPO', 'EPO', 'POS', 'HDHP'];
  resultList = ['POS', 'NEG', 'None'];

  // start pagination declaration
  pageSize = 30;
  // pageFilterSizes = [20, 50, 100];
  // totalPageCount = 5;
  // end pagination declaration

  defaultFilters = { 
    patientsFilterType: patientsIdTypes.active,
    clientsFilterType: clients.active,
    clinicalQFilterType: workBenchClinicalQ.cases,
    inventoriesFilterType: workBenchInventories.equipments,
    openCasesFilterType: ClientServicesOpenCases.toxicology,
    billingReviewFilterType: ClientServicesBillingQ.toxQ,
  };
  
  idleTime = 1380; //
  idleInterval: any;

  ejsGridHeight = 'calc(100vh - 196px)';

  constructor(
    private toastr: ToastrService,
    private location: LocationStrategy,
    private router: Router,
    private http: HttpClient,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
  ) { }

  setLogOutTimer() {
    this.idleInterval = setInterval(() => { this.timerIncrement(); }, 1000); // 1 second

    // reset the idle timer.
    document.onclick = (e) => {
      this.resetIdleTime();
    };
    document.onkeypress = (e) => {
      this.resetIdleTime();
    };
  }

  resetIdleTime() {
    this.idleTime = 1380;
    document.title = this.siteName;
  }

  timerIncrement() {
    this.idleTime = this.idleTime - 1;
    if (this.idleTime < 600) { 
      //display message if the user is idle more than 10min
      document.title = this.secondsToHms(this.idleTime) + ' until your session times out!';
      if (this.idleTime == 0) { 
        this.resetAllData();
        this.router.navigate(['/login']);
      }
    } 
  }

  secondsToHms(d) {
    d = Number(d);
    const h = Math.floor(d / 3600);
    const m = Math.floor(d % 3600 / 60);
    const s = Math.floor(d % 3600 % 60);

    const hDisplay = h > 0 ? h + (h == 1 ? ':' : ':') : '00:';
    const mDisplay = m > 0 ? m + (m == 1 ? ':' : ':') : '00:';
    const sDisplay = s > 0 ? s + (s == 1 ? '' : '') : '00';
    return mDisplay + sDisplay;
  }

  setDataToStorage(key: string, value: string) {
    sessionStorage.setItem(this.secureData(key), this.secureData(value));
  }

  getDataFromStorage(key: string) {
    const data = sessionStorage.getItem(this.secureData(key));
    if (data) {
      return this.unSecureData(data);
    }
    return null;
  }

  removeDataFromStorage(key: string) {
    sessionStorage.removeItem(this.secureData(key));
  }

  // commented as backup , can be removed after permission will be stable.
 /*  setMainMenu() {
    this.currentUserRole = new UserRoles();
    if (this.currentUser && this.currentUser.LabclientId == 0) {
      this.currentUserRole.admin = true;
      this.mainMenu = MenuList;

      var permissionDropdownMenuItems = [];      
      if (this.currentUser.Permissions) {
        this.currentUser.Permissions.forEach(value => {
          var permission = this.mainMenu.filter(item => item.name.replace(/\s/g, "").toLowerCase() == value.Action.toLowerCase());
          if (permission && permission.length > 0) {
            permissionDropdownMenuItems.push(permission[0]);
          }
        });
        
        permissionDropdownMenuItems.forEach(menuName => {
          var submenus = menuName.child;
          submenus.forEach(subMenuName => {
            if (subMenuName.action) {
              var hPermission = this.hasPermission(subMenuName.action);
              if (hPermission === false) {
                menuName.child.splice(submenus.indexOf(subMenuName), 1);
              }
            }
            // var subMenuPermission = menuName.child.find(n => n.action.toLowerCase() == value.Action.toLowerCase());
          });

        });
        this.mainMenu = permissionDropdownMenuItems;
      } else {
        this.mainMenu = MenuList;
      }

      return this.mainMenu;
      // return MenuList;
    } else {
      if (this.currentUser && this.currentUser.PatientId && this.currentUser.PatientId > 0) {
        this.currentUserRole.patient = true;
        this.mainMenu = PatientMenuList;
        return PatientMenuList;
      } else {
        this.currentUserRole.client = true;
        if (this.hasPermission('covidorder')) {
          const covidUrl = '/lab-order/covid-order/' + '/' + 0 + '/new-order';
          ClientMenuList[0].child[1].url = covidUrl;
        } else {
          const url = '/lab-order/order/' + 0 + '/' + 0 + '/' + 0 + '/' + false + '/new-order';
          ClientMenuList[0].child[1].url = url;
        }
        this.mainMenu = ClientMenuList;
        return ClientMenuList;
      }
    }
  } */

  setMainMenu() {
    this.currentUserRole = new UserRoles();
    if (this.currentUser && this.currentUser.LabclientId == 0) {
      this.currentUserRole.admin = true;
      this.mainMenu = JSON.parse(JSON.stringify(MenuList));

      const permissionDropdownMenuItems = [];
      if (this.currentUser.Permissions) {
        this.currentUser.Permissions.forEach(value => {
          const permission = this.mainMenu.filter(item => item.name.replace(/\s/g, '').toLowerCase() == value.Action.toLowerCase());
          if (permission && permission.length > 0) {
            permissionDropdownMenuItems.push(permission[0]);
          }
        });
        permissionDropdownMenuItems.forEach(menuName => {
          const filteredMenus = [];
          const submenus = menuName.child;
          submenus.forEach(subMenuName => {
            let permission = false;
            let otherPermissionOne = false;
            if (subMenuName.action) // this is first level action permission check
            {
              permission = this.hasPermission(subMenuName.action);
              if (permission === true)
              {
                filteredMenus.push(subMenuName);
              }
              else if (subMenuName.otherActionOne) // this is second level action permission check, if first one fail, second condition check
              {
                otherPermissionOne = this.hasPermission(subMenuName.otherActionOne);
                if (otherPermissionOne === true)
                {
                  filteredMenus.push(subMenuName);
                }
              }
            }
            else{
              filteredMenus.push(subMenuName);
            }
            menuName.child = filteredMenus;
          });
        });
        this.mainMenu = permissionDropdownMenuItems;
      } else {
        this.mainMenu = MenuList;
      }

      return this.mainMenu;
      // return MenuList;
    } else {
      if (this.currentUser && this.currentUser.PatientId && this.currentUser.PatientId > 0) {
        this.currentUserRole.patient = true;
        this.mainMenu = PatientMenuList;
        return PatientMenuList;
      }
       else if (this.currentUser && this.currentUser.LabclientId > 0){
        this.currentUserRole.client = true;
        this.clientMenu =  JSON.parse(JSON.stringify(ClientMenuList));
        const permissionDropdownMenuItemsClients = [];
        if (this.currentUser.Permissions) {
            if (this.clientMenu.length > 0) {
              // permissionDropdownMenuItemsClients = this.clientMenu;
               this.currentUser.Permissions.forEach(value => {
                const permission = this.clientMenu.filter(item => item.name.replace(/\s/g, '').toLowerCase() == value.Action.toLowerCase());
                if (permission && permission.length > 0) {
                  permissionDropdownMenuItemsClients.push(permission[0]);
                }
              });
            }
            permissionDropdownMenuItemsClients.forEach(menuName => {
            const filteredMenus = [];
            const submenus = menuName.child;
            submenus.forEach(subMenuName => {
              if (subMenuName.action) {
                const hPermission = this.hasPermission(subMenuName.action);
                if (hPermission === true) {
                  if (subMenuName.orderType){
                    if (subMenuName.name == 'New Order' && subMenuName.orderType == 'covid' && this.hasPermission('covidorder')){
                      filteredMenus.push(subMenuName);
                    }else if (subMenuName.name == 'New Order' && subMenuName.orderType == 'regular' && this.hasPermission('covidorder') == false){
                      filteredMenus.push(subMenuName);
                    }
                  }
                 else{
                    filteredMenus.push(subMenuName);
                  }
                }
              }else{
                filteredMenus.push(subMenuName);
              }
              menuName.child = filteredMenus;
            });
          });
        }
        this.mainMenu = permissionDropdownMenuItemsClients;
        return permissionDropdownMenuItemsClients;
      }
    }
  }
  getCurrentUserDetails(url) {
    const user = this.getDataFromStorage('user');
    if (user) {
      this.currentUser = user;
    } else {
      this.resetAllData();
      if (url != '/login') {
        this.router.navigate(['/login']);
      }
    }
  }

  resetAllData() {
    document.title = this.siteName;
    clearInterval(this.idleInterval);
    this.dialog.closeAll();
    this.currentUser = null;
    this.userPermissions = [];
    sessionStorage.clear();
  }

  goBack() {
    this.location.back();
  }

  getBreadcrumbHistory() {
    let history = null;
    const breadcrumbHistory = this.getDataFromStorage('breadcrumbHistory');
    if (breadcrumbHistory) { history = breadcrumbHistory; }
    return history;
  }

  downloadFile(data: any) {
   // const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
   // const url = window.URL.createObjectURL(blob);
   // window.open(data);
    window.location.href = data;
  }


  getExcelFile(b64Data, sliceSize=512) { 
    let byteCharacters = atob(b64Data); //data.file there
    let byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        let slice = byteCharacters.slice(offset, offset + sliceSize);
    
        let byteNumbers = new Array(slice.length);
        for (var i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        let byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }
    var currentBlob=new Blob(byteArrays, {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
    var pdfUrl = URL.createObjectURL(currentBlob);
    return pdfUrl;
    }

  showToastr(type: 'success' | 'warning' | 'error' | 'info', message: string, title: string = '') {
    if (type == 'error') {
      const toastSetting: Partial<IndividualConfig> = {
        progressBar: true,
        enableHtml: true,
        disableTimeOut: true,
        titleClass: 'toasterTitle',
        messageClass: 'toasterMessage',
        positionClass: 'toast-top-center',
      };
      this.toastr.show(message, (title ? title : type.toTitleCase()), toastSetting, `toast-${type}`);
    } else {
      const toastSetting: Partial<IndividualConfig> = {
        progressBar: true,
        enableHtml: true,
        timeOut: 10000,
        titleClass: 'toasterTitle',
        messageClass: 'toasterMessage',
        positionClass: 'toast-top-center',
      };
      this.toastr.show(message, (title ? title : type.toTitleCase()), toastSetting, `toast-${type}`);
    }
  }

  closeToaster() {
    this.toastr.clear();
  }

  secureData(data: any) {
    data = JSON.stringify(data);
    const str = 'fl!23net@%$$2!@#';
    const key = CryptoJS.enc.Utf8.parse(str);
    const iv = CryptoJS.enc.Utf8.parse(str);
    const secure = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(data), key,
      {
        keySize: 128 / 8,
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
    return secure.toString();
  }

  unSecureData(data: any) {
    const str = 'fl!23net@%$$2!@#';
    const key = CryptoJS.enc.Utf8.parse(str);
    const iv = CryptoJS.enc.Utf8.parse(str);
    const unSecure = CryptoJS.AES.decrypt(data, key,
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    return JSON.parse(unSecure.toString(CryptoJS.enc.Utf8));
  }

  hasPermission(Action: string) {
    if (Action) {
      if (!this.userPermissions || this.userPermissions.length == 0) {
        if (this.currentUser) { this.userPermissions = this.currentUser.Permissions; }
      }
      if (Action == 'toxicology')
      {
        if (!this.currentUser.Toxicology) {
          return false;
        }
      }
      if (Action == 'clinical')
      {
        if (!this.currentUser.Clinical) {
          return false;
        }
      }
      if (Action == 'molecular')
      {
        if (!this.currentUser.Molecular) {
          return false;
        }
      }
      if (Action == 'molecularpcr'){
        if (!this.currentUser.Molecular) {
          return false;
        }
      }
      return this.userPermissions.some(m => m.Action.toLowerCase() == Action.toLowerCase());
    } else {
      return false;
    }
  }

  getBase64Encoded(file: File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      let base64: string;
      reader.onload = async () => {
        base64 = reader.result.toString().split(',')[1];
        resolve(base64);
      };
      reader.onerror = async (error) => {
        reject(error);
      };
    });
  }

  getPdf(documentDataBase64: any, documentType: string = '') {
    const binaryImg = atob(documentDataBase64);
    const length = binaryImg.length;
    const arrayBuffer = new ArrayBuffer(length);
    const uintArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < length; i++) {
      uintArray[i] = binaryImg.charCodeAt(i);
    }

    let contentType = '';
    if (documentType) {
      contentType = this.convertFileTypeToContentType(documentType);
    } else {
      contentType = 'application/pdf';
    }

    const currentBlob = new Blob([uintArray], { type: contentType });
    const pdfUrl = URL.createObjectURL(currentBlob);
    return pdfUrl;
  }

  convertFileTypeToContentType(documentType) {
    let contentType = '';
    documentType = documentType.trim();
    documentType = documentType.toLowerCase();
    if (documentType == 'png') { contentType = 'image/png'; }
    if (documentType == 'jpg') { contentType = 'image/jpg'; }
    if (documentType == 'jpeg') { contentType = 'image/jpeg'; }
    if (documentType == 'pdf') { contentType = 'application/pdf'; }
    if (documentType == 'txt') { contentType = 'text/plain'; }
    if (documentType == 'doc') { contentType = 'application/msword'; }
    if (documentType == 'docx') { contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'; }
    if (documentType == 'zip') { contentType = 'application/zip'; }
    return contentType;
  }

  encryptpassword(password: string) {
    const key = CryptoJS.enc.Utf8.parse('8080808080808080');
    const ivv = CryptoJS.enc.Utf8.parse('8080808080808080');
    const encryptedpassword = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(password), key, { keySize: 128 / 8, iv: ivv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 });
    return encryptedpassword.toString();
  }

  resetPageSettings() {
    return { currentPage: 1, pageSize: this.pageSize };
    // return { currentPage: 1, pageSize: this.pageSize, pageSizes: this.pageFilterSizes, pageCount: this.totalPageCount };
  }

  dataStateChange(state, dataModel): Promise<any> {
    // console.log(state);
    return new Promise((resolve, reject) => {
      if (state.action.requestType !== 'refresh') {
        const data = {
          dataModel: {
            PageSize: '',
            StartPageNo: '',
            SortExpression: '',
          },
          pageSettings: null
        };

        if (dataModel.PageSize !== 'PageSize=' + state.take) {
          data.dataModel.PageSize = 'PageSize=' + (state.take || this.pageSize);
          data.pageSettings = { currentPage: 1 };
          data.dataModel.StartPageNo = 'StartPageNo=' + 1;
        } else {
          data.dataModel.PageSize = 'PageSize=' + (state.take || this.pageSize);
        }

        if (state.sorted && state.sorted.length > 0) {
          data.dataModel.SortExpression = ('SortExpression=' + ((state.sorted[0].name) + '+' + (state.sorted[0].direction == 'ascending' ? 'asc' : 'desc')));
        } else {
          data.dataModel.SortExpression = 'SortExpression=';
        }

        if (state.action.currentPage) {
          data.dataModel.StartPageNo = 'StartPageNo=' + state.action.currentPage;
        } else {
          data.pageSettings = { currentPage: 1 };
          data.dataModel.StartPageNo = 'StartPageNo=' + 1;
        }

        resolve(data);
      }
    });
  }

  setCustomFilter(state) {
    let customFilter = '';
    if (state && state.where && state.where.length > 0) {
      let filter = '';
      state.where[0].predicates.forEach(element => { filter += 'Filter.' + element.field + '=' + element.value + '&'; });
      customFilter = filter;
    }
    return customFilter;
  }

  setCustomFilterForRole(state) {
    let customFilter = '';
    if (state && state.where && state.where.length > 0) {
      let filter = '';
      state.where[0].predicates.forEach(element => 
        { 
          element.value = (element.value.toString().toLowerCase() == 'no' ? false : element.value.toString().toLowerCase() == 'yes' ? true : element.value);
          filter += 'Filter.' + element.field + '=' + element.value + '&'; 
        });
      customFilter = filter;
    }
    return customFilter;
  }

  // showPagination() {
  //   const gridElement: any = document.getElementsByClassName('e-gridpager');
  //   if (gridElement) {
  //     for (const item of gridElement) {
  //       const rowCountElement = item.getElementsByClassName('e-pagecountmsg');
  //       const count = rowCountElement[0].innerText.match(/(\d+)/)[0];
  //       if (count <= this.pageSize) {
  //         const element = item.getElementsByClassName('e-pagercontainer') as HTMLCollectionOf<HTMLElement>;
  //         element[0].style.display = 'none';
  //         const element1 = item.getElementsByClassName('e-pagesizes') as HTMLCollectionOf<HTMLElement>;
  //         element1[0].style.display = 'none';
  //         gridElement[0].style.display = 'inline';
  //       } else {
  //         const element = item.getElementsByClassName('e-pagercontainer') as HTMLCollectionOf<HTMLElement>;
  //         element[0].style.display = 'inline-block';
  //         const element1 = item.getElementsByClassName('e-pagesizes') as HTMLCollectionOf<HTMLElement>;
  //         element1[0].style.display = 'inline';
  //         gridElement[0].style.display = 'inline-block';
  //       }
  //     }
  //   }
  // }

  getEjsGridData(url, params?, requestType?: 'post') {
    if (requestType == 'post') {
      return this.http.post<any>(url, params).pipe(
        map(m => {
          return { result: m.data || [], count: m.totalRecords || 0 };
        }),
        catchError(err => {
          return of({ result: [], count: 0 });
        })
      );
    } else {
      if (params) {
        return this.http.get<any>(url, { params }).pipe(
          map(m => {
            return { result: m.data || [], count: m.totalRecords || 0 };
          }),
          catchError(err => {
            return of({ result: [], count: 0 });
          })
        );
      } else {
        return this.http.get<any>(url).pipe(
          map(m => {
            return { result: m.data || [], count: m.totalRecords || 0 };
          }),
          catchError(err => {
            return of({ result: [], count: 0 });
          })
        );
      }
    }
  }

  sendErrors(errors, showInPoup = true) {
    const errorMSG = [];
    errors.forEach(data => {
      Object.keys(data.constraints).forEach(data3 => {
        errorMSG.push({ label: data3, errMsg: data.constraints[data3] });
      });
    });
    if (errorMSG && errorMSG.length > 0) {
      let strError = '';
      errorMSG.forEach(element => {
        strError += '- ' + element.errMsg + ' <br/>';
      });
      if (strError != '') {
        if (showInPoup) {
          this.showPopupErrorMessage.emit({ error: strError, displayError: true });
        }
        else {
          this.showErrorMessage.emit({ error: strError, displayError: true });
        }

        //this.showToastr('error', strError);
      }
    }
  }

  viewCaseInfo(caseId, isCaseOrderInfo = false) {
    const dialogRef = this.dialog.open(CaseInfoPopUpComponent, {
      width: '80%',
      disableClose: true,
      data: { caseId, isCaseOrderInfo }
    });
  }

  viewClientInfo(clientId) {
    const dialogRef = this.dialog.open(ClientDetailsComponent, {
      width: '80%',
      disableClose: true,
      data: { clientId }
    });
  }

  viewPatientInfo(patientId) {
    const dialogRef = this.dialog.open(PatientDetailsComponent, {
      width: '90%',
      disableClose: true,
      data: { patientId }
    });
  }

  viewPatientLog(item) {
    const dialogRef = this.dialog.open(PatientLogDialogComponent, {
      width: '80%',
      disableClose: true,
      data: item || null,
    });
  }

  viewPhysicianInfo(physicianId) {
    const dialogRef = this.dialog.open(PhysicianDetailsComponent, {
      width: '70%',
      disableClose: true,
      data: { physicianId }
    });
  }

  showLogs(item) {
    const dialogRef = this.dialog.open(ViewLogsComponent, {
      width: '70%',
      disableClose: true,
      data: item || null
    });
  }

  viewPatientHistory(patientId) {
    const dialogRef = this.dialog.open(ViewPatientHistoryComponent, {
      width: '70%',
      disableClose: true,
      data: { patientId }
    });
  }

  innerHtmlSanitizer(innerHtml) {
    return innerHtml ? this.sanitizer.bypassSecurityTrustHtml(innerHtml) : '';
  }

  isValidZip(zip) {
    const regex = /^(\d{5}(?:\d{4})?)$/;
    const status = regex.test(zip);
    return status ? false : true;
  }

  isValidPhoneNumber(phoneNumber) {
    const regex = /^(\+\d{1,3}[- ]?)?\d{10}$/;
    const status = regex.test(phoneNumber);
    return status ? false : true;
  }

  isValidSSN(ssn) {
    const regex = /^[0-9]{9}$/;
    const status = regex.test(ssn);
    return status ? false : true;
  }

  isValidEmailAddress(emailAddress) {
    const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const status = regex.test(emailAddress);
    return status ? false : true;
  }

  isValidAdditionalFax(data) {
    const regex = /^(?:\s*\d{10}\s*(?:,|$))+$/;
    const status = regex.test(data);
    return status ? false : true;
  }
  removeHTMLTags(str) {
    if ((str === null) || (str === '')) {
    return false;
    }
    else {
    str = str.toString();
    }
    return str.replace( /(<([^>]+)>)/ig, '');
    }

    getZip(result) {
      var binaryImg = atob(result);
      var length = binaryImg.length;
      var arrayBuffer = new ArrayBuffer(length);
      var uintArray = new Uint8Array(arrayBuffer);
      for (var i = 0; i < length; i++) {
        uintArray[i] = binaryImg.charCodeAt(i);
      }
      var currentBlob = new Blob([uintArray], { type: 'application/zip' });
      var pdfUrl = URL.createObjectURL(currentBlob);
      return pdfUrl;
    }

}
